<script>
import {Link} from "@inertiajs/vue3";
import NumberFormatComponent from "@js/components/Formatter/NumberFormatComponent.vue";
import IconCartPlus from "@js/components/Icons/IconCartPlus.vue";
import IconHeart from "@js/components/Icons/IconHeart.vue";
import IconHeartFilled from "@js/components/Icons/IconHeartFilled.vue";
import ImageLoader from "@js/components/Loaders/ImageLoader.vue";
import IconScales from "@js/components/Icons/IconScales.vue";
import {useLikeStore} from "@js/store/LikeStore.js";
import {useCompareStore} from "@js/store/CompareStore.js";
import {useCartStore} from "@js/store/CartStore.js";
import IconCheckMark from "@js/components/Icons/IconCheckMark.vue";
import OnlyDesktop from "@js/components/DynamicComponents/OnlyDesktop.vue";
import OnlyMobile from "@js/components/DynamicComponents/OnlyMobile.vue";
import {computed} from "vue";
import CartUnitSwitch from "@js/components/Sections/Cart/Component/CartUnitSwitch.vue";
import CartQuantityStepper from "@js/components/Sections/Cart/Component/CartQuantityStepper.vue";
import {useCatalogStore} from "@js/store/CatalogStore.js";
import IconInfo from "@js/components/Icons/IconInfo.vue";
import IconTrash from "@js/components/Icons/IconTrash.vue";
import {useProductStore} from "@js/store/ProductStore.js";
import {ecomClick, mapProduct} from "@js/ecommerce.js";

export default {
    name: "CatalogProductItemWide",
    components: {
        IconTrash,
        IconInfo,
        CartQuantityStepper,
        CartUnitSwitch,
        OnlyMobile,
        OnlyDesktop,
        IconCheckMark,
        IconScales,
        NumberFormatComponent,
        IconCartPlus,
        IconHeart,
        IconHeartFilled,
        ImageLoader,
        Link,
    },
    props: {
        data: {type: Object, required: true},
        cart_mode: {type: Boolean, default: false},
        defaultMode: {type: String, default: "m"}, // "m" | "t" | "pc"
        listName: {type: String, default: 'Каталог'},
        position: {type: Number, default: 1},
    },
    methods: {
        trackClick() {
            try {
                ecomClick(mapProduct(this.data, {list: this.listName, position: this.position}))
            } catch (e) {
            }
        }
    },
    setup(props) {
        const likeStore = useLikeStore();
        const compareStore = useCompareStore();
        const cartStore = useCartStore();
        const catalogStore = useCatalogStore();
        const productStore = useProductStore();

        // Регистрация продукта и дефолтного режима для UI-состояния (m|t|pc)
        const initialMode = props.data.price_per_piece ? 'pc' : props.defaultMode;
        cartStore.registerCatalogProduct(props.data, initialMode);

        const modeProxy = computed({
            get: () => cartStore.uiOf(props.data.id).mode,
            set: (val) => cartStore.setMode(props.data.id, val),
        });

        const qtyProxy = computed({
            get: () => cartStore.uiOf(props.data.id).qty,
            set: (val) => cartStore.setQtyFromInput(props.data.id, val),
        });

        // Текущая текстовая метка единицы измерения
        const unitText = computed(() => {
            const u = cartStore.unitLabel(props.data.id); // 'м' | 'т' | 'шт'
            if (u === 'м') return '/метр';
            if (u === 'т') return '/тонна';
            return '/шт';
        });

        // Есть ли хоть какая-то цена (для показа прайс-блока)
        const hasAnyPrice = computed(() =>
            !!(props.data.price_per_meter || props.data.price_per_ton || props.data.price_per_piece)
        );

        const canBuy = computed(
            () => props.data.available && cartStore.priceActive(props.data.id)
        );

        return {
            catalogStore,
            likeStore,
            compareStore,
            productStore,
            cartStore,
            modeProxy,
            qtyProxy,
            unitText,
            hasAnyPrice,
            canBuy,
        };
    },
};
</script>

<template>
    <div class="relative w-full bg-white overflow-hidden"
         :class="[cart_mode? '': 'border border-gray-200 rounded-2xl']">
        <!-- ============ MOBILE (<=1024) ============ -->
        <OnlyMobile :maxSize="1024">
            <div class="p-3">
                <div class="grid grid-cols-[100px_1fr] gap-3 items-start">
                    <!-- Фото + бейджи -->
                    <div class="relative">
                        <Link :href="route('product', data.slug)" @pointerdown="trackClick"
                              class="block w-[100px] h-[100px] rounded-lg border border-gray-200 overflow-hidden">
                            <ImageLoader
                                :title="data.title"
                                :src="'/storage/assets/products/'+data.category_slug+'/preview/'+ (data.images.preview_square || data.images.preview_wide)"
                                imgClasses="w-full h-full object-cover"/>
                        </Link>

                        <div class="absolute left-0 top-0 flex gap-1">
                            <span v-if="data.is_promo"
                                  class="rounded bg-amber-400 px-1.5 py-[1px] text-[10px] font-medium text-black">Акция</span>
                            <span v-if="data.is_hit"
                                  class="rounded bg-green-600 px-1.5 py-[1px] text-[10px] font-medium text-white">Хит</span>
                        </div>
                    </div>

                    <!-- Заголовок + метки + лайк/сравнение -->
                    <div class="min-w-0">
                        <div class="flex items-start justify-between gap-2">
                            <div class="w-min text-nowrap text-[10px] px-1.5 py-[2px] rounded"
                                 :class="[data.available ? 'bg-emerald-800 text-white': 'bg-amber-400 text-black']">
                                {{ data.available ? 'В наличии' : 'Ожидаем поставку' }}
                            </div>

                            <div class="flex shrink-0 gap-1">
                                <button v-if="!cart_mode"
                                        class="inline-flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-white"
                                        @click.prevent="compareStore.toggleCompare(data.id)"
                                        :aria-pressed="compareStore.isCompared(data.id)"
                                        :title="compareStore.isCompared(data.id) ? 'Убрать из сравнения' : 'В сравнение'">
                                    <IconScales
                                        :class="['w-4 h-4', compareStore.isCompared(data.id) ? 'text-blue-500' : 'text-gray-700']"/>
                                </button>
                                <button v-if="!cart_mode"
                                        class="inline-flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-white"
                                        @click.prevent="likeStore.toggleLike(data.id)"
                                        :aria-pressed="likeStore.isLiked(data.id)"
                                        :title="likeStore.isLiked(data.id) ? 'Убрать из избранного' : 'В избранное'">
                                    <IconHeartFilled v-if="likeStore.isLiked(data.id)" class="w-4 h-4 text-likeColor"/>
                                    <IconHeart v-else class="w-4 h-4 text-gray-700"/>
                                </button>
                                <button v-if="cart_mode"
                                        class="group inline-flex h-8 w-8 shadow-sm items-center justify-center rounded-full border border-gray-200 bg-white hover:bg-gray-50 transition"
                                        @click.prevent="cartStore.remove(data.id, cartStore.uiOf(data.id).mode)"
                                        title="Удалить">
                                    <IconTrash class="h-4 w-4 text-gray-700 md:group-hover:text-likeColor transition"/>
                                </button>
                            </div>
                        </div>

                        <Link :href="route('product', data.slug)" @pointerdown="trackClick"
                              class="my-1.5 block text-sm leading-[16px] font-medium text-gray-900 hover:text-navHoverColor transition line-clamp-2">
                            {{ data.title }}
                        </Link>
                        <div v-if="data.sub_category"
                             class="text-nowrap w-min rounded text-black bg-grayBg px-2 py-1 text-[11px] font-medium">
                            {{ data.sub_category }}
                        </div>
                    </div>
                </div>

                <!-- Цена + переключатель -->
                <div class="mt-2">
                    <template v-if="hasAnyPrice">
                        <div class="flex items-center justify-between gap-2">
                            <CartUnitSwitch v-if="!data.price_per_piece"
                                            v-model="modeProxy"
                                            class="scale-90 origin-left"
                                            :disabled-m="!data.price_per_meter"
                                            :disabled-t="!data.price_per_ton"
                                            :disabled-pc="!data.price_per_piece"/>
                            <div v-else></div>

                            <div class="text-right">
                                <div class="text-[12px] text-gray-700 leading-tight">
                                    <NumberFormatComponent
                                        :number="cartStore.priceActive(data.id)"
                                        prepend_text="Цена: "
                                        append_text=" ₽"
                                        class="text-sm font-medium text-black"/>
                                    <span class="text-[11px] font-medium">{{ unitText }}</span>
                                </div>
                            </div>
                        </div>
                    </template>
                    <template v-else>
                        <div class="font-bold leading-tight">Цена договорная</div>
                    </template>
                </div>

                <!-- Количество + CTA / или запрос цены -->
                <div v-if="canBuy" class="mt-2">
                    <div class="flex items-center justify-between gap-5">
                        <CartQuantityStepper v-model="qtyProxy"
                                             :min="cartStore.minQty(data.id)"
                                             :step="cartStore.step(data.id)"
                                             :disabled="!canBuy"
                                             size="sm"
                                             width-class="w-[112px]"
                                             @inc="cartStore.incQty(data.id)"
                                             @dec="cartStore.decQty(data.id)"/>
                        <NumberFormatComponent :number="cartStore.totalPrice(data.id)"
                                               prepend_text="Итого: "
                                               append_text=" ₽"
                                               class="text-sm font-medium"/>
                    </div>
                </div>
                <!-- Параметры -->
                <div v-if="canBuy" class="mt-3 flex flex-wrap gap-1.5 text-[11px] font-medium text-gray-700">
                    <span v-if="cartStore.lengthM(data.id)" class="rounded bg-grayBg px-1.5 py-[2px]">
                        Итоговая длина:
                        <NumberFormatComponent :number="cartStore.lengthM(data.id)" append_text=" м" :animate="false"/>
                    </span>
                    <span v-if="cartStore.weightKg(data.id)" class="rounded bg-grayBg px-1.5 py-[2px]">
                        Итоговый вес:
                        <NumberFormatComponent :number="cartStore.weightKg(data.id)" append_text=" кг"
                                               :animate="false"/>
                    </span>
                </div>
                <div v-if="!cart_mode" class="mt-3">
                    <template v-if="!canBuy">
                        <!--                    <div class="flex flex-col gap-2">-->
                        <!--                        <div class="rounded bg-grayBg px-2 py-2 text-xs leading-tight font-medium">-->
                        <!--                            <p>-->
                        <!--                                Оставьте контакты — подберём цену под вашу задачу. Зафиксируем стоимость на сутки.-->
                        <!--                            </p>-->
                        <!--                        </div>-->
                        <button @click.prevent="productStore.showFastBuyPopup(data.id)"
                                class="inline-flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-amber-400 text-sm font-semibold text-black transition hover:bg-amber-300 disabled:cursor-not-allowed disabled:opacity-50">
                            Узнать цену
                        </button>
                        <!--                    </div>-->
                    </template>
                    <template v-else>
                        <div class="flex gap-2">
                            <template v-if="cartStore.isInCartExact(data.id)">
                                <!-- Уже в корзине (в текущей единице) -->
                                <Link :href="route('cart')"
                                      class="inline-flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-emerald-600 text-sm font-semibold text-white transition hover:bg-emerald-500">
                                    <IconCheckMark class="w-5 h-5"/>
                                    Уже в корзине
                                </Link>
                            </template>
                            <template v-else>
                                <!-- Ещё нет в корзине -->
                                <button
                                    class="inline-flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-baseColor text-sm font-semibold text-white transition hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-50"
                                    :disabled="!canBuy"
                                    @click="cartStore.addToCartFromCard(data.id)">
                                    <IconCartPlus class="h-5 w-5"/>
                                    В корзину
                                </button>
                            </template>
                            <button @click.prevent="productStore.showFastBuyPopup(data.id)"
                                    class="inline-flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-black/10 border-black/10 text-sm font-semibold text-black transition md:hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-50">
                                Купить сейчас
                            </button>
                        </div>
                    </template>
                </div>
            </div>
        </OnlyMobile>

        <!-- ============ DESKTOP (как было) ============ -->
        <OnlyDesktop :maxSize="1024">
            <div class="p-3 md:p-4">
                <div class="flex h-full gap-4 flex-col md:flex-row md:gap-6">
                    <!-- Картинка + бейджи -->
                    <Link :href="route('product', data.slug)" @pointerdown="trackClick"
                          class="relative w-full md:w-40">
                        <div class="overflow-hidden w-full rounded-xl border border-gray-200">
                            <ImageLoader :title="data.title"
                                         :src="'/storage/assets/products/'+data.category_slug+'/preview/'+ data.images.preview_square"
                                         imgClasses="w-full h-full object-contain"/>
                        </div>
                        <div class="absolute left-0 top-0 flex gap-1">
                            <span v-if="data.is_promo"
                                  class="rounded bg-amber-400 px-2 py-0.5 text-[11px] font-medium text-black">Акция</span>
                            <span v-if="data.is_hit"
                                  class="rounded bg-green-600 px-2 py-0.5 text-[11px] font-medium text-white">Хит</span>
                        </div>
                    </Link>

                    <!-- Центр -->
                    <div class="flex min-w-0 grow flex-col gap-1 py-0">
                        <div class="w-min text-nowrap text-[11px] px-2 py-1 rounded leading-none"
                             :class="[data.available ? 'bg-emerald-800 text-white': 'bg-amber-400 text-black']">
                            {{ data.available ? 'В наличии' : 'Ожидаем поставку' }}
                        </div>
                        <Link :href="route('product', data.slug)" @pointerdown="trackClick"
                              class="max-w-[400px] md:line-clamp-2 text-sm md:text-base font-medium leading-5 text-gray-900 hover:text-navHoverColor transition">
                            {{ data.title }}
                        </Link>
                        <div v-if="data.sub_category"
                             class="text-nowrap w-min rounded text-black bg-grayBg px-2 py-1 text-[11px] font-medium">
                            {{ data.sub_category }}
                        </div>

                        <div class="mt-auto">
                            <div v-if="hasAnyPrice" class="flex flex-col gap-1.5">
                                <div>
                                    <CartUnitSwitch v-if="!data.price_per_piece"
                                                    v-model="modeProxy"
                                                    :disabled-m="!data.price_per_meter"
                                                    :disabled-t="!data.price_per_ton"
                                                    :disabled-pc="!data.price_per_piece"/>
                                </div>
                                <div class="text-sm text-gray-700">
                                    <NumberFormatComponent
                                        :number="cartStore.priceActive(data.id)"
                                        prepend_text="Цена: "
                                        append_text=" ₽"
                                        class="text-base font-semibold text-black"/>
                                    <span class="font-medium text-sm">{{ unitText }}</span>
                                </div>
                            </div>
                            <div v-else class="font-medium leading-tight">
                                Цена договорная
                            </div>
                        </div>
                    </div>


                    <!-- Правая колонка -->
                    <div class="flex flex-col items-stretch mt-auto gap-3 pt-0 w-full md:w-[360px]">
                        <div v-if="canBuy" class="flex max-md:flex-col gap-3"
                             :class="[cart_mode ? 'flex-col items-end' : 'md:items-center']">
                            <CartQuantityStepper v-model="qtyProxy"
                                                 :min="cartStore.minQty(data.id)"
                                                 :step="cartStore.step(data.id)"
                                                 :disabled="!canBuy"
                                                 size="md"
                                                 width-class="w-32"
                                                 @inc="cartStore.incQty(data.id)"
                                                 @dec="cartStore.decQty(data.id)"
                                                 :class="{'order-2': cart_mode}"/>
                            <div class="text-base font-semibold text-gray-900"
                                 :class="{'order-1': cart_mode}">
                                <NumberFormatComponent :number="cartStore.totalPrice(data.id)"
                                                       prepend_text="Итого: "
                                                       append_text=" ₽"/>
                            </div>
                        </div>
                        <div v-if="canBuy" class="flex flex-wrap gap-2 text-xs font-medium text-gray-700">
                            <span v-if="cartStore.lengthM(data.id)" class="rounded bg-grayBg px-2 py-1">
                                Итоговая длина: <NumberFormatComponent :number="cartStore.lengthM(data.id)"
                                                                       append_text=" м" :animate="false"/>
                            </span>
                            <span v-if="cartStore.weightKg(data.id)" class="rounded bg-grayBg px-2 py-1">
                                Итоговый вес: <NumberFormatComponent :number="cartStore.weightKg(data.id)"
                                                                     append_text=" кг" :animate="false"/>
                            </span>
                        </div>
                        <template v-if="!cart_mode">
                            <template v-if="!canBuy">
                                <div class="flex flex-col gap-2">
                                    <div class="rounded bg-grayBg px-2 py-2 text-xs leading-tight font-medium">
                                        <p>Оставьте контакты — подберём цену под вашу задачу. Зафиксируем стоимость на
                                            сутки.</p>
                                    </div>
                                    <button @click.prevent="productStore.showFastBuyPopup(data.id)"
                                            class="inline-flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-amber-400 text-base font-semibold text-black transition hover:bg-amber-300 disabled:cursor-not-allowed disabled:opacity-50">
                                        Узнать цену
                                    </button>
                                </div>
                            </template>
                            <template v-else>
                                <div class="flex gap-3">
                                    <template v-if="cartStore.isInCartExact(data.id)">
                                        <!-- Уже в корзине (в текущей единице) -->
                                        <Link :href="route('cart')"
                                              class="inline-flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-emerald-600 text-sm font-semibold text-white transition hover:bg-emerald-500">
                                            <IconCheckMark class="w-5 h-5"/>
                                            Уже в корзине
                                        </Link>
                                    </template>
                                    <template v-else>
                                        <!-- Ещё нет в корзине -->
                                        <button
                                            class="inline-flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-baseColor text-sm font-semibold text-white transition hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-50"
                                            :disabled="!canBuy"
                                            @click="cartStore.addToCartFromCard(data.id)">
                                            <IconCartPlus class="h-5 w-5"/>
                                            В корзину
                                        </button>
                                    </template>
                                    <button @click.prevent="productStore.showFastBuyPopup(data.id)"
                                            class="inline-flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-black/10 border-black/10 text-sm font-semibold text-black transition md:hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-50">
                                        Купить сейчас
                                    </button>
                                </div>
                            </template>
                        </template>
                    </div>

                    <!-- Избранное/Сравнение -->
                    <div class="absolute flex gap-2 end-4 top-4">
                        <button v-if="!cart_mode"
                                class="group inline-flex h-9 w-9 shadow-sm items-center justify-center rounded-full border border-gray-200 bg-white hover:bg-gray-50"
                                @click.prevent="compareStore.toggleCompare(data.id)"
                                :aria-pressed="compareStore.isCompared(data.id)"
                                :title="compareStore.isCompared(data.id) ? 'Убрать из сравнения' : 'В сравнении'">
                            <IconScales v-if="compareStore.isCompared(data.id)"
                                        class="min-w-5 min-h-5 max-w-5 max-h-5 mb-[3px] text-blue-500"/>
                            <IconScales v-else
                                        class="min-w-5 min-h-5 max-w-5 max-h-5 mb-[3px] text-gray-700 group-hover:text-blue-500 transition"/>
                        </button>
                        <button v-if="!cart_mode"
                                class="group inline-flex h-9 w-9 shadow-sm items-center justify-center rounded-full border border-gray-200 bg-white hover:bg-gray-50"
                                @click.prevent="likeStore.toggleLike(data.id)"
                                :aria-pressed="likeStore.isLiked(data.id)"
                                :title="likeStore.isLiked(data.id) ? 'Убрать из избранного' : 'В избранное'">
                            <IconHeartFilled v-if="likeStore.isLiked(data.id)" class="h-5 w-5 text-likeColor"/>
                            <IconHeart v-else class="h-5 w-5 text-gray-700 group-hover:text-likeColor transition"/>
                        </button>
                        <button v-if="cart_mode"
                                class="group inline-flex h-9 w-9 shadow-sm items-center justify-center rounded-full border border-gray-200 bg-white hover:bg-gray-50"
                                @click.prevent="cartStore.remove(data.id, cartStore.uiOf(data.id).mode)"
                                title="Удалить">
                            <IconTrash class="h-5 w-5 text-gray-700 md:group-hover:text-likeColor transition"/>
                        </button>
                    </div>
                </div>
            </div>
        </OnlyDesktop>
    </div>
</template>

<style scoped>
.line-clamp-2 {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    overflow: hidden;
    -webkit-line-clamp: 2;
}

/* Чуть плотнее кнопки/контролы на мобильном */
:deep(.segmented-sm) {
    font-size: 12px
}
</style>


